import React, {useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {SettingsStackParamList} from '@/app/routes';
import {fetchCompany, updateCompany} from '@/entities/company/api';
import {
  asOrganizationType,
  DEFAULT_ORGANIZATION_TYPE,
  ORGANIZATION_TYPES,
  organizationTypeLabelKey,
  type OrganizationType,
} from '@/entities/company/orgType';
import {useAuthStore} from '@/entities/session/auth-store';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Field, FieldGroup} from '@/shared/ui/field';
import {FormRow} from '@/shared/ui/form';
import {AppInput} from '@/shared/ui/input';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {ChoiceChips} from '@/shared/ui/segmented';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * The company's own card — name, classifier, contact details.
 *
 * Two fields the gateway accepts are deliberately NOT here. `notes` is operator-only:
 * a write from a company domain is discarded in SILENCE, so a field for it would show
 * an edit that never lands. `owner_account_id` is platform-managed: a transfer from
 * here is a 403. Both are documented in entities/company/api.ts; neither belongs in a
 * company owner's UI.
 *
 * Deleting a company is not offered either. It is reachable through the API, but a
 * button that ends a tenant — its people, its terminals' bindings, its history — does
 * not belong two taps from a settings form; that is an operator action, taken with a
 * human on the other end.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'CompanySettings'>;

const styles = StyleSheet.create({
  form: {gap: spacing(4)},
});

export function CompanySettingsScreen(_: Props) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const context = useAuthStore(s => s.activeContext);
  const reloadContexts = useAuthStore(s => s.reloadContexts);
  const companyId = context?.companyId;

  const company = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => fetchCompany(companyId as string),
    enabled: !!companyId,
  });

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [orgType, setOrgType] = useState<OrganizationType>(DEFAULT_ORGANIZATION_TYPE);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!company.data) {
      return;
    }
    setName(company.data.name);
    setAddress(company.data.address ?? '');
    setEmail(company.data.email ?? '');
    setPhone(company.data.phone ?? '');
    setOrgType(asOrganizationType(company.data.organization_type));
  }, [company.data]);

  const save = useMutation({
    mutationFn: () =>
      updateCompany(companyId as string, {
        name,
        address,
        email,
        phone,
        organization_type: orgType,
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({queryKey: ['company', companyId]});
      // The company's name is the label on the context switcher and the app header,
      // and those come from the session store, not from this query.
      await reloadContexts();
    },
  });

  if (!companyId) {
    return <ContextPlaceholder titleKey="more.companySettings" />;
  }

  return (
    <Page
      sideNav={<SettingsNav />}
      width="form"
      scroll
      testID="company-settings"
      title={t('more.companySettings')}
      >

      {company.isPending ? (
        <Skeleton height={220} radius={radii.lg} />
      ) : company.isError ? (
        <Banner tone="error" title={t('errors.generic')} />
      ) : (
        <>
          <Section title={t('company.details')}>
            <Card padding={4} style={styles.form}>
              <AppInput
                label={t('company.name')}
                value={name}
                onChangeText={value => {
                  setName(value);
                  setSaved(false);
                }}
                testID="company-name"
              />
              <AppInput
                label={t('company.address')}
                value={address}
                onChangeText={value => {
                  setAddress(value);
                  setSaved(false);
                }}
                testID="company-address"
              />
              <FormRow>
                <AppInput
                  label={t('company.email')}
                  value={email}
                  onChangeText={value => {
                    setEmail(value);
                    setSaved(false);
                  }}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  testID="company-email"
                />
                <AppInput
                  label={t('company.phone')}
                  value={phone}
                  onChangeText={value => {
                    setPhone(value);
                    setSaved(false);
                  }}
                  autoCapitalize="none"
                  keyboardType="phone-pad"
                  testID="company-phone"
                />
              </FormRow>
              {save.isError ? (
                <Banner
                  tone="error"
                  title={
                    save.error instanceof ApiError
                      ? save.error.message
                      : t('errors.generic')
                  }
                />
              ) : null}
              {saved ? <Banner tone="success" title={t('account.saved')} /> : null}
              <AppButton
                title={t('actions.save')}
                loading={save.isPending}
                disabled={name.trim().length === 0}
                onPress={() => save.mutate()}
                testID="company-save"
              />
            </Card>
          </Section>

          {/* The sector, and it is EDITABLE — not a read-only line as it was until
              2026-08-15. Creation used to hard-code `other` with no way to correct it
              afterwards, so every company made from the cabinet stayed «Прочее»
              permanently, and the operator console's filter by sector quietly lumped
              them all together while looking perfectly correct. */}
          <Section
            title={t('company.organizationType')}
            hint={t('company.organizationTypeHint')}>
            <ChoiceChips
              options={ORGANIZATION_TYPES.map(value => ({
                value,
                label: t(organizationTypeLabelKey(value)),
              }))}
              value={orgType}
              onChange={value => {
                setOrgType(value);
                setSaved(false);
              }}
              testID="company-org-type"
            />
          </Section>

          <Section title={t('company.identifiers')}>
            <FieldGroup>
              <Field label={t('company.id')} value={company.data.id} mono />
              <Field label={t('company.kind')} value={company.data.kind} />
              <Field
                label={t('company.owner')}
                value={company.data.owner?.full_name ?? company.data.owner_account_id}
                note={t('company.ownerNote')}
              />
            </FieldGroup>
          </Section>
        </>
      )}
    </Page>
  );
}
